import React, { useCallback, useMemo, useState } from 'react';
import {
  AddCircleOutlineRounded,
  EditNote,
  ForwardToInboxRounded,
  InputRounded,
  PersonRounded,
  RefreshRounded,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { Button, IconButton, Tooltip } from '@mui/material';
import Grid from '@mui/material/Grid';
import NotesManager from 'shared/components/data-display/notesManager';
import { NewTabLink } from 'shared/components/abstract/interactables';
import { CardControls, CardSection } from 'shared/components/cardControls';
import ArchiveSwitch from 'shared/components/ArchiveSwitch';
import {
  createTenantUnitReference,
  sendTenantWelcomeEmail,
  updateTenant,
} from 'shared/api/tenants';
import { setUnitQueue } from 'shared/api/networking';
import {
  createOnboardAccount,
  getOnboardMimic,
  searchOnboardAccount,
  updateOnboardAccount,
} from 'features/tenant/api/onboard';
import { dateString, formatPhoneNumber, getStripeDataUrl } from 'shared/utils';
import { BoxWrapper, GridWrapper } from 'shared/components/data-display/GridFormatter';
import { DataFormatter } from 'shared/components/data-display/DataFormatter';
import {
  getBuildingPath, getComplexPath, getManagerPath, getUnitPath,
} from 'shared/routes/building';
import {
  useAuth, useConfig, useNotification, useRequest,
} from 'shared/hooks';
import HTML from 'shared/components/HTML';
import { TextAreaEditor, TextEditor } from 'shared/components/data-display/editors';
import { DataReducerActions } from 'shared/reducers';
import { getTenantMovePath } from 'shared/routes/tenant';
import { validateTenantData } from './helper';

/**
 * @param {Object} props
 * @param {number} props.tenantId - Tenant ID
 */
function OrphanFallback({ tenantId }) {
  return (
    <>
      <p>
        This tenant cannot be restored to their previous location.
      </p>
      <p>
        To activate this account again,
        <Link to={getTenantMovePath(null, null, tenantId)}>{' move them '}</Link>
        to a new location.
      </p>
    </>
  );
}

/**
 * @param {Object} props
 * @param {Function} props.tenantDispatch - Dispatch function for tenant state
 * @param {Object} props.tenantState - Tenant state object
 * @param {Function} props.reloadTenant - Function to reload tenant data
 * @param {Function} props.reloadBuilding - Function to reload building data
 */
function TenantView(props) {
  const {
    tenantDispatch,
    tenantState,
    reloadTenant,
    reloadBuilding,
  } = props;

  const { data: tenant } = tenantState;
  const { onboard_account: onboardAccount } = tenant;
  const buildingData = tenant.archivedAt ? tenantState.previousBuilding : tenantState.building;
  const { request, isLoading } = useRequest();
  const { user } = useAuth();
  const notification = useNotification();
  const { onboardURL } = useConfig();
  const [editingNotes, setEditingNotes] = useState(false);

  const unitId = tenantState.building ? tenantState.building.unit.id : null;
  const tenantId = tenantState.data.id;
  const archiveRequest = tenant.archivedAt
    ? createTenantUnitReference(tenant.id, tenant.previousUnitId, user.username)
    : createTenantUnitReference(tenant.id, null, user.username);

  const assessOnboardAccountContacts = useCallback(async (
    dataToTest,
    onboardAccountFuncCallback,
  ) => {
    if (('email' in dataToTest && dataToTest.email !== '') || ('phone' in dataToTest && dataToTest.phone !== '')) {
      return request(searchOnboardAccount(dataToTest.email, dataToTest.phone))
        .then((response) => {
          if (response.total === 1 && response.rows[0].tenant_id === tenantId) {
            return true;
          }
          if (response.total > 0) {
            notification.pushError('Onboard account already exists for contact information');
            if (response.rows.find((row) => dataToTest.email !== '' && row.registered_email === dataToTest.email)) {
              notification.pushError(`Email ${dataToTest.email} already attached to account`);
            }
            if (response.rows.find((row) => dataToTest.phone !== '' && row.registered_phone === dataToTest.phone)) {
              notification.pushError(`Phone ${dataToTest.phone} already attached to account`);
            }
            return false;
          }
          return true;
        })
        .then((account) => {
          if (account) {
            return onboardAccountFuncCallback();
          }
          return null;
        });
    }
    return onboardAccountFuncCallback();
  }, [request, notification, tenantId]);

  const saveChanges = async () => {
    const message = validateTenantData(
      { ...tenantState.data, ...tenantState.updatedData },
      (!!tenant.phone || !!tenant.email), // Prevent user from removing contact when it exists.
    );
    if (message) {
      notification.pushError(message);
      return;
    }
    if (tenant.onboard_account?.id
      && ('email' in tenantState.updatedData || 'phone' in tenantState.updatedData)) {
      const onboardAccountUpdates = { id: tenant.onboard_account.id };
      if ('email' in tenantState.updatedData) {
        onboardAccountUpdates.registered_email = tenantState.updatedData.email;
      }
      if ('phone' in tenantState.updatedData) {
        onboardAccountUpdates.registered_phone = formatPhoneNumber(tenantState.updatedData.phone);
      }

      const updatedOnboardAccount = await assessOnboardAccountContacts(
        tenantState.updatedData,
        () => request(
          updateOnboardAccount(onboardAccountUpdates),
          'Updated Onboard Account',
        ),
      );

      if (!updatedOnboardAccount) {
        return;
      }
    }

    const updatedTenant = await request(updateTenant(tenantId, tenantState.updatedData), 'Updated tenant');
    if (updatedTenant) {
      tenantDispatch({
        action: 'tenant',
        // Don't overwrite the extra tenant state (username, first_time)
        payload: { ...tenantState.data, ...updatedTenant },
      });
    }
  };

  const isDot1x = tenantState.building && tenantState.building?.auth_method.includes('802.1x');

  const tenantDescriptions = [
    {
      label: 'First Name',
      key: 'first_name',
      editor: TextEditor,
    },
    {
      label: 'Last Name',
      key: 'last_name',
      editor: TextEditor,
    },
    {
      label: 'Email',
      key: 'email',
      editor: TextEditor,
    },
    {
      label: 'Phone',
      key: 'phone',
      formatter: () => formatPhoneNumber(tenant.phone),
      editor: TextEditor,
    },
    {
      label: 'Alternative Phone',
      key: 'alt_phone',
      formatter: () => formatPhoneNumber(tenant.alt_phone),
      editor: TextEditor,
    },
  ];

  const notesDescription = [
    {
      label: 'Legacy Notes',
      key: 'note',
      formatter: (t) => <HTML content={t.note} />,
      editor: TextAreaEditor,
    },
  ];

  const usernameDescription = useMemo(
    () => {
      const description = {
        label: 'Onboard Account',
        skipWhen: () => !isDot1x && !onboardAccount,
        readOnly: true,
      };

      if (!onboardAccount?.id) {
        const createAccount = async () => {
          if (!tenant.email && !tenant.phone) {
            notification.pushError('Email address or phone required to create Onboard Account');
            return;
          }

          const result = await assessOnboardAccountContacts(
            tenant,
            () => request(
              createOnboardAccount(tenantId),
              'Onboard account successfully created',
            ),
          );

          if (result) {
            tenantDispatch({
              action: 'onboard_account',
              payload: result,
            });
          }
        };

        const actionBtn = (
          <Tooltip title="Register Onboard Account">
            <IconButton onClick={createAccount} color="primary">
              <AddCircleOutlineRounded />
            </IconButton>
          </Tooltip>
        );
        return {
          ...description,
          formatter: () => 'Not Created',
          actionButton: actionBtn,
        };
      }

      if (!tenantState.editEnabled || !tenantState.building) {
        return {
          ...description,
          formatter: () => onboardAccount?.registered_email,
        };
      }

      const manageAccount = async () => {
        const mimicToken = await request(getOnboardMimic(user.id, tenant.id));
        if (!mimicToken?.id) {
          notification.pushError('Failed to login to Onboard');
          return;
        }

        // Create a <form> element that submits a POST request to Onboard's auth controller.
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = `${onboardURL}/login`;
        form.target = '_BLANK';

        const account = document.createElement('input');
        account.type = 'hidden';
        account.name = 'login[email]';
        account.value = tenant.onboard_account.registered_email;

        const mimic = document.createElement('input');
        mimic.type = 'hidden';
        mimic.name = 'mimic[auth]';
        mimic.value = `Bearer ${mimicToken.token}`;

        form.appendChild(account);
        form.appendChild(mimic);

        document.body.appendChild(form);
        form.submit();
        document.body.removeChild(form);
      };

      const actionBtn = (
        <Tooltip title="Open the tenant's onboard account in a new tab">
          <IconButton onClick={manageAccount} color="primary">
            <PersonRounded />
          </IconButton>
        </Tooltip>
      );
      return {
        ...description,
        formatter: () => `Registered: ${(onboardAccount?.registered_email != null
        && onboardAccount?.registered_email !== ''
          ? onboardAccount?.registered_email
          : onboardAccount?.registered_phone)}`,
        actionButton: actionBtn,
      };
    },
    [
      onboardAccount, tenantState.editEnabled, tenantState.building, isDot1x, tenant, tenantId,
      assessOnboardAccountContacts, notification, onboardURL, request, tenantDispatch, user.id,
    ],
  );

  const welcomeEmailDescription = useMemo(() => {
    let value;
    const to = onboardAccount?.welcome_email_sent_to;
    const at = onboardAccount?.welcome_email_sent_at;
    if (!to) {
      value = 'Not Sent';
    } else {
      value = `${to} (${dateString(at)})`;
    }
    const description = {
      label: 'Welcome Email',
      skipWhen: () => !isDot1x || !onboardAccount?.id,
      readOnly: true,
      formatter: () => value,
    };

    if (!tenantState.editEnabled || !tenantState.building) return description;

    const actionBtn = onboardAccount?.registered_email ? (
      <Tooltip title="Send the welcome email">
        <IconButton
          color="primary"
          onClick={async () => {
            const result = await request(sendTenantWelcomeEmail(tenant.id), 'Sent email');
            if (result) reloadTenant();
          }}
        >
          <ForwardToInboxRounded />
        </IconButton>
      </Tooltip>
    ) : undefined;
    return { ...description, actionButton: actionBtn };
  }, [isDot1x, reloadTenant, request, tenant, tenantState, onboardAccount]);

  const internetDescription = useMemo(() => {
    let text;
    const currentDescription = tenant.currentQos.description;
    if (tenantState.building?.auth_method === 'None') {
      text = 'None - building has no auth method';
    } else if (tenant.currentQos.speed === 0) {
      text = 'None - non amenity';
    } else if (tenant.subscription) {
      text = `${currentDescription} ${tenant.subscription.voucher ? 'voucher' : 'subscription'}`;
    } else {
      text = `${currentDescription} amenity service`;
    }

    const description = {
      label: 'Internet',
      skipWhen: () => !tenantState.building,
      readOnly: true,
      formatter: () => text,
    };
    if (!tenantState.canAccessNetworking || !isDot1x) {
      return description;
    }

    const actionBtn = (
      <Tooltip title="Set tenant's VTR queue to current speed">
        <IconButton
          color="primary"
          onClick={async () => {
            const response = await request(setUnitQueue(unitId));
            if (response) {
              // The notification can be incorrect if the unit has multiple subs.
              // Upgrader will choose the fastest, but this will just print the tenants current.
              if (response.updated_count > 0) {
                notification.pushSuccess(`Changed queue speed to ${currentDescription}`);
              } else {
                notification.pushInfo(`Already set to ${currentDescription}`);
              }
            }
          }}
        >
          <RefreshRounded />
        </IconButton>
      </Tooltip>
    );
    return {
      ...description,
      actionButton: actionBtn,
    };
  }, [isDot1x, notification, request, tenant, tenantState, unitId]);

  const actionDescriptions = [
    usernameDescription,
    welcomeEmailDescription,
    {
      skipWhen: () => !isDot1x || !onboardAccount?.id,
      label: 'Onboard Wizard',
      formatter: (data) => (data?.onboard_account?.wizard_complete ? 'Completed' : 'Not Complete'),
    },
    internetDescription,
    {
      label: 'Search Stripe',
      skipWhen: () => !tenant.email || !tenantState.editEnabled,
      readOnly: true,
      formatter: () => (
        <NewTabLink text={`Data for "${tenant.email}"`} link={getStripeDataUrl(tenant.email)} />
      ),
    },
    {
      label: 'Updated At',
      formatter: () => dateString(tenant.updated_at),
    },
  ];

  const unitDescription = useMemo(() => {
    const actionBtn = (
      <Tooltip title="Move tenant">
        <IconButton
          color="primary"
          href={getTenantMovePath(buildingData?.id, buildingData?.unit?.id, tenantId)}
        >
          <InputRounded />
        </IconButton>
      </Tooltip>
    );
    const link = (
      <Link to={getUnitPath(buildingData?.id, buildingData?.unit?.id)}>
        {buildingData?.unit?.name}
      </Link>
    );

    return {
      label: 'Unit',
      formatter: () => link,
      actionButton: actionBtn,
    };
  }, [tenantId, buildingData]);

  const locationDescriptions = [
    {
      label: 'City',
      formatter: (building) => `${building.city}, ${building.state}, ${building.zip}`,
    },
    {
      label: 'Manager',
      formatter: (building) => (
        <Link to={getManagerPath(building.manager.id)}>{building.manager.name}</Link>
      ),
    },
    {
      label: 'Complex',
      formatter: (building) => (
        <Link to={getComplexPath(building.complex.id)}>{building.complex.name}</Link>
      ),
    },
    {
      label: 'Building',
      formatter: (building) => (
        <Link to={getBuildingPath(building.id)}>{building.name}</Link>
      ),
    },
    unitDescription,
    {
      label: 'VLAN',
      formatter: (building) => building.unit.vlan,
      skipWhen: (building) => !building?.auth_method.includes('802.1x'),
    },
    {
      label: 'QOS Management',
      formatter: (building) => building.unit.qos_management,
      skipWhen: (building) => !building?.auth_method.includes('802.1x'),
    },
    {
      label: 'Service Type',
      key: 'service_type',
    },
    {
      label: 'Auth Method',
      key: 'auth_method',
    },
  ];

  return (
    <CardSection editEnabled={tenantState.editEnabled} saveChanges={saveChanges}>
      <CardControls
        clearChanges={() => tenantDispatch({ action: DataReducerActions.clear })}
        disabled={isLoading}
        dirty={tenantState.dirty}
        editEnabled={tenantState.editEnabled}
      >
        <ArchiveSwitch
          afterToggle={reloadBuilding}
          archivedAt={tenant.archivedAt}
          disabled={!tenantState.previousBuilding && !tenantState.building}
          requestBody={archiveRequest}
          showToggle={tenantState.editEnabled}
        />
      </CardControls>
      <BoxWrapper>
        <Grid container spacing={2}>
          <GridWrapper size={{ md: 12, lg: 3 }} title="Tenant">
            <DataFormatter
              descriptions={tenantDescriptions}
              editing={tenantState.editEnabled}
              data={tenantState.data}
              updatedData={tenantState.updatedData}
              updateValue={(key, value) => tenantDispatch({
                action: DataReducerActions.update,
                payload: { key, value },
              })}
            />
          </GridWrapper>
          <GridWrapper
            size={{ md: 12, lg: 3 }}
            title="Notes"
            titleIcon={(
              <Button
                variant="outlined"
                startIcon={<EditNote />}
                onClick={() => {
                  if (!tenant.id) return;
                  setEditingNotes(true);
                }}
                size="small"
              >
                Edit
              </Button>
            )}
          >
            <NotesManager
              entityType="Tenant"
              entityID={tenant.id}
              isEditing={editingNotes}
              onStopEditing={() => setEditingNotes(false)}
              onStartEditing={() => setEditingNotes(true)}
              legacyNotes={tenant.note}
              entityConfig={{
                body: tenant,
                key: 'note',
                update: updateTenant,
                dispatch: tenantDispatch,
              }}
            />
            {tenant?.note && (
              <DataFormatter
                descriptions={notesDescription}
                editing={false}
                data={tenantState.data}
                updatedData={tenantState.updatedData}
                updateValue={(key, value) => tenantDispatch({
                  action: 'update',
                  payload: {
                    key,
                    value,
                  },
                })}
                multiline
                copyable
              />
            )}
          </GridWrapper>
          <GridWrapper size={{ md: 12, lg: 3 }} title="Account Management">
            <DataFormatter
              descriptions={actionDescriptions}
              data={tenantState.data}
              fallback={<OrphanFallback tenantId={tenantId} />}
            />
          </GridWrapper>
          <GridWrapper
            size={{ md: 12, lg: 3 }}
            title={tenant.archivedAt === undefined ? 'Location' : 'Previous Location'}
          >
            <DataFormatter
              descriptions={locationDescriptions}
              data={buildingData}
              fallback={<OrphanFallback tenantId={tenantId} />}
            />
          </GridWrapper>
        </Grid>
      </BoxWrapper>
    </CardSection>
  );
}

export default TenantView;
